import {AfterViewInit, Component, OnInit, TemplateRef, ViewChild, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {FieldService, ReportService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {FarmerRecommendationListItemDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/farmerRecommendationListItemDto';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/dictionaries';
import {TableColumn} from '@components/data-table/data-table.component';
import {ListTableFiltersWrapper} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper';
import {ListTableFiltersWrapperParent} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {RegionDisplay} from '@components/region-display/region-display';

import {CropCacheService} from '@services/caches/crop-cache.service';
import {buildFilterQuery, replaceFilter} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';
import {ReportDownloadActionComponent} from '@app/pages/crop-monitoring/components/report-download-action/report-download-action.component';
import {FieldPassportLinkComponent} from '@components/field-passport-link/field-passport-link.component';
import {RequestParams} from '@components/select/resource-component';
import {SortType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {
  reportFieldId,
  reportFieldName,
  reportRegionId,
  reportSeasonCropId,
} from './farmer-recommendations-report.util';
import {ANOMALY_LEGEND_ITEMS} from '../anomalies/anomaly-type-colors';

const ROW_KIND_SATELLITE = 'satellite';
const FILTER_SOURCE_QWEN = 'qwen';
const FILTER_SOURCE_SATELLITE = 'satellite';

/** Qwen problem_type filter — labels via `names` (no ngx-translate instant / uz fallback on first load). */
const FARMER_PROBLEM_TYPE_FILTER_OPTIONS: ReadonlyArray<{
  id: string;
  names: Record<string, string>;
}> = [
  {
    id: 'disease',
    names: {
      'ru-RU': 'Болезнь',
      'en-US': 'Disease',
      'uz-Latn-UZ': 'Kasallik',
      'fr-FR': 'Maladie',
      'ar-SA': 'مرض',
      'ar-LB': 'مرض',
    },
  },
  {
    id: 'pest',
    names: {
      'ru-RU': 'Вредитель',
      'en-US': 'Pest',
      'uz-Latn-UZ': 'Zararkunanda',
      'fr-FR': 'Ravageur',
      'ar-SA': 'آفة',
      'ar-LB': 'آفة',
    },
  },
  {
    id: 'element_deficiency',
    names: {
      'ru-RU': 'Дефицит элементов',
      'en-US': 'Deficiency of elements',
      'uz-Latn-UZ': 'Elementlar tanqisligi',
      'fr-FR': 'Carence en éléments',
      'ar-SA': 'نقص العناصر',
      'ar-LB': 'نقص العناصر',
    },
  },
  {
    id: 'disease_and_pest',
    names: {
      'ru-RU': 'Болезнь и вредитель',
      'en-US': 'Disease and pest',
      'uz-Latn-UZ': 'Kasallik va zararkunanda',
      'fr-FR': 'Maladie et ravageur',
      'ar-SA': 'مرض وآفة',
      'ar-LB': 'مرض وآفة',
    },
  },
  {
    id: 'complex',
    names: {
      'ru-RU': 'Комплексная аномалия',
      'en-US': 'Complex anomaly',
      'uz-Latn-UZ': 'Kompleks anomaliya',
      'fr-FR': 'Anomalie complexe',
      'ar-SA': 'شذوذ مركّب',
      'ar-LB': 'شذوذ مركّب',
    },
  },
  {
    id: 'undefined',
    names: {
      'ru-RU': 'Не определено',
      'en-US': 'Undefined',
      'uz-Latn-UZ': 'Aniqlanmagan',
      'fr-FR': 'Non défini',
      'ar-SA': 'غير محدد',
      'ar-LB': 'غير محدد',
    },
  },
];

/** Satellite anomaly type filter options (1-6, without ComplexAnomaly). */
const SATELLITE_ANOMALY_FILTER_OPTIONS: ReadonlyArray<{
  id: number;
  names: Record<string, string>;
}> = ANOMALY_LEGEND_ITEMS
  .filter(item => item.anomalyType !== 7)
  .map(item => ({
    id: item.anomalyType,
    names: {
      'ru-RU': ruLabel(item.labelKey),
      'en-US': enLabel(item.labelKey),
      'uz-Latn-UZ': uzLabel(item.labelKey),
      'fr-FR': frLabel(item.labelKey),
      'ar-SA': arLabel(item.labelKey),
      'ar-LB': arLabel(item.labelKey),
    },
  }));

function ruLabel(key: string): string {
  const map: Record<string, string> = {
    ANOMALY_TYPE_FLOODING: 'Подтопление',
    ANOMALY_TYPE_OVERWATERING: 'Переувлажнение',
    ANOMALY_TYPE_DROUGHT: 'Засуха',
    ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS: 'Пропуск всходов',
    ANOMALY_TYPE_PREMATURE_AGING: 'Раннее старение',
    ANOMALY_TYPE_NUTRITIONAL_STRESS: 'Питательный стресс',
    ANOMALY_TYPE_COMPLEX_ANOMALY: 'Комплексная аномалия',
  };
  return map[key] ?? key;
}

function enLabel(key: string): string {
  const map: Record<string, string> = {
    ANOMALY_TYPE_FLOODING: 'Flooding',
    ANOMALY_TYPE_OVERWATERING: 'Overwatering',
    ANOMALY_TYPE_DROUGHT: 'Drought',
    ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS: 'Skipping of seedlings',
    ANOMALY_TYPE_PREMATURE_AGING: 'Premature aging',
    ANOMALY_TYPE_NUTRITIONAL_STRESS: 'Nutritional stress',
    ANOMALY_TYPE_COMPLEX_ANOMALY: 'Complex anomaly',
  };
  return map[key] ?? key;
}

function uzLabel(key: string): string {
  const map: Record<string, string> = {
    ANOMALY_TYPE_FLOODING: 'Toshqin',
    ANOMALY_TYPE_OVERWATERING: 'Haddan tashqari namlanish',
    ANOMALY_TYPE_DROUGHT: "Qurg'oqchilik",
    ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS: "Ko'chatlarni o'tkazib yuborish",
    ANOMALY_TYPE_PREMATURE_AGING: 'Erta qarish',
    ANOMALY_TYPE_NUTRITIONAL_STRESS: 'Oziq moddalar stressi',
    ANOMALY_TYPE_COMPLEX_ANOMALY: 'Murakkab anomaliya',
  };
  return map[key] ?? key;
}

function frLabel(key: string): string {
  const map: Record<string, string> = {
    ANOMALY_TYPE_FLOODING: 'Inondation',
    ANOMALY_TYPE_OVERWATERING: 'Surirrigation',
    ANOMALY_TYPE_DROUGHT: 'Sécheresse',
    ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS: 'Manque de plants',
    ANOMALY_TYPE_PREMATURE_AGING: 'Vieillissement prématuré',
    ANOMALY_TYPE_NUTRITIONAL_STRESS: 'Stress nutritionnel',
    ANOMALY_TYPE_COMPLEX_ANOMALY: 'Anomalie complexe',
  };
  return map[key] ?? key;
}

function arLabel(key: string): string {
  const map: Record<string, string> = {
    ANOMALY_TYPE_FLOODING: 'فيضان',
    ANOMALY_TYPE_OVERWATERING: 'ري زائد',
    ANOMALY_TYPE_DROUGHT: 'جفاف',
    ANOMALY_TYPE_SKIPPING_OF_SEEDLINGS: 'تساقط البذور',
    ANOMALY_TYPE_PREMATURE_AGING: 'شيخوخة مبكرة',
    ANOMALY_TYPE_NUTRITIONAL_STRESS: 'إجهاد غذائي',
    ANOMALY_TYPE_COMPLEX_ANOMALY: 'شذوذ مركب',
  };
  return map[key] ?? key;
}

@Component({
  selector: 'app-farmer-recommendations-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ListTableFiltersWrapper,
    RegionDisplay,
    ReportDownloadActionComponent,
    FieldPassportLinkComponent,
  ],
  templateUrl: './farmer-recommendations-page.html',
  styleUrl: './farmer-recommendations-page.scss',
})
export class FarmerRecommendationsPageComponent
  extends ListTableFiltersWrapperParent<FarmerRecommendationListItemDto, ReportService>
  implements OnInit, AfterViewInit
{
  readonly service = inject(ReportService);
  readonly fieldService = inject(FieldService);
  readonly cropService = inject(CropService);
  readonly cropCache = inject(CropCacheService);
  private readonly regionManager = inject(RegionManagerService);

  override getAllMethod = 'cropMonitoringReportGetFarmerRecommendationsPost';
  override getTotalMethod = 'cropMonitoringReportGetFarmerRecommendationsCountPost';
  override storageKey = 'crop_monitoring_farmer_recommendations_table';
  override filtersForms: IMsfFormBuilder[] = [];

  readonly defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    sortBy: 'Date',
    sort: SortType.NUMBER_1,
    filters: [],
  };

  tableColumns: TableColumn[] = [];

  @ViewChild('regionTemplate', {static: true}) regionTemplate!: TemplateRef<unknown>;
  @ViewChild('fieldTemplate', {static: true}) fieldTemplate!: TemplateRef<unknown>;
  @ViewChild('cropTemplate', {static: true}) cropTemplate!: TemplateRef<unknown>;
  @ViewChild('sourceTemplate', {static: true}) sourceTemplate!: TemplateRef<unknown>;
  @ViewChild('anomalyCellTemplate', {static: true}) anomalyCellTemplate!: TemplateRef<unknown>;
  @ViewChild('downloadTemplate', {static: true}) downloadTemplate!: TemplateRef<unknown>;

  override ngOnInit(): void {
    super.ngOnInit();
    this.cropCache.getCrops().subscribe();
    this.initFiltersForm();
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      {field: 'date', header: 'Date_recommendation', type: 'date', width: '140px'},
      {
        field: '',
        header: 'Region',
        type: 'custom',
        customTemplate: this.regionTemplate,
        width: '180px',
      },
      {
        field: '',
        header: 'Field name',
        type: 'custom',
        customTemplate: this.fieldTemplate,
        minWidth: '140px',
      },
      {
        field: '',
        header: 'Culture',
        type: 'custom',
        customTemplate: this.cropTemplate,
        width: '160px',
      },
      {
        field: '',
        header: 'FARMER_RECOMMENDATIONS_SOURCE',
        type: 'custom',
        customTemplate: this.sourceTemplate,
        width: '140px',
      },
      {
        field: '',
        header: 'FARMER_RECOMMENDATIONS_ANOMALY_DESCRIPTION',
        type: 'custom',
        customTemplate: this.anomalyCellTemplate,
        minWidth: '220px',
      },
      {
        field: '',
        header: 'FARMER_RECOMMENDATIONS_REPORT_LINK',
        type: 'custom',
        customTemplate: this.downloadTemplate,
        width: '72px',
        sortable: false,
      },
    ];
  }

  protected initFiltersForm(): void {
    this.filtersForms = [
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManager,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {treeBy: 'parentId'},
        },
        placeholder: 'Select region',
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.fieldService,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none',
          },
        },
      },
      {
        name: 'cropId',
        title: 'Culture',
        type: 'select-paginate',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        placeholder: 'Select',
        options: {
          select: {
            optionValue: 'id',
            translateBy: 'names',
            searchBy: 'names',
            filter: [
              {
                filterField: 'isMonitoring',
                filterOperator: FilterOperator.Equals,
                filterValue: 'true',
              },
            ],
          },
        },
      },
      {
        name: 'createAt',
        title: 'Date_recommendation',
        type: 'date',
        placeholder: 'Select period',
        options: {
          date: {selectionMode: 'range', dateOnly: true},
        },
      },
      {
        name: 'sourceKind',
        title: 'FARMER_RECOMMENDATIONS_SOURCE',
        type: 'select',
        placeholder: 'SELECT_SOURCE',
        selectOptions: [
          {
            id: FILTER_SOURCE_QWEN,
            names: {
              'ru-RU': 'Наземное обследование',
              'en-US': 'Ground survey',
              'uz-Latn-UZ': 'Yer tekshiruvi',
              'fr-FR': 'Inspection au sol',
              'ar-SA': 'المسح الأرضي',
              'ar-LB': 'المسح الأرضي',
            },
          },
          {
            id: FILTER_SOURCE_SATELLITE,
            names: {
              'ru-RU': 'Спутниковые данные',
              'en-US': 'Satellite data',
              'uz-Latn-UZ': "Sun'iy yo'ldosh ma'lumotlari",
              'fr-FR': 'Données satellitaires',
              'ar-SA': 'بيانات الأقمار الصناعية',
              'ar-LB': 'بيانات الأقمار الصناعية',
            },
          },
        ],
        options: {
          clearable: true,
          select: {optionValue: 'id', translateBy: 'names'},
        },
      },
      {
        name: 'anomalyType',
        title: 'FARMER_ANOMALY_TYPE_GROUND',
        type: 'select',
        placeholder: 'ANOMALIES_FILTER_ANOMALY_TYPE_PLACEHOLDER',
        selectOptions: [...FARMER_PROBLEM_TYPE_FILTER_OPTIONS],
        hide: () => this.filterModel?.sourceKind === FILTER_SOURCE_SATELLITE,
        options: {
          clearable: true,
          select: {optionValue: 'id', translateBy: 'names'},
        },
      },
      {
        name: 'satelliteAnomalyType',
        title: 'FARMER_ANOMALY_TYPE_SATELLITE',
        type: 'select',
        placeholder: 'SELECT_SATELLITE_ANOMALY_TYPE',
        selectOptions: [...SATELLITE_ANOMALY_FILTER_OPTIONS],
        hide: () => this.filterModel?.sourceKind === FILTER_SOURCE_QWEN,
        options: {
          clearable: true,
          select: {optionValue: 'id', translateBy: 'names'},
        },
      },
    ];
  }

  /** `dateOnly` picker value → `yyyy-MM-dd` (stays a string in API JSON). */
  private asDateOnlyString(raw: unknown): string | null {
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
      return raw.trim();
    }
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      const m = String(raw.getMonth() + 1).padStart(2, '0');
      const d = String(raw.getDate()).padStart(2, '0');
      return `${raw.getFullYear()}-${m}-${d}`;
    }
    return null;
  }

  private addDaysDateOnly(yyyyMmDd: string, days: number): string {
    const [y, m, d] = yyyyMmDd.split('-').map((part) => parseInt(part, 10));
    const next = new Date(y, m - 1, d + days);
    return this.asDateOnlyString(next)!;
  }

  private appendCreateAtRangeFilters(filters: FieldFilter[], range: unknown): void {
    const items = Array.isArray(range) ? range : range != null && range !== '' ? [range] : [];
    const start = this.asDateOnlyString(items[0]);
    if (!start) {
      return;
    }
    const end = this.asDateOnlyString(items[1] ?? items[0]) ?? start;

    replaceFilter(
      'createAt',
      {
        filterField: 'createAt',
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: start,
      },
      filters,
    );
    filters.push({
      filterField: 'createAt',
      filterOperator: FilterOperator.LessThan,
      filterValue: this.addDaysDateOnly(end, 1),
    });
  }

  override async applyFilters(): Promise<void> {
    const model = {...this.filterModel} as Record<string, unknown>;
    const range = model['createAt'];
    delete model['createAt'];

    // Drop hidden filter values so they don't leak into the request
    const sourceKind = model['sourceKind'];
    if (sourceKind === FILTER_SOURCE_SATELLITE) {
      delete model['anomalyType'];
    }
    if (sourceKind === FILTER_SOURCE_QWEN) {
      delete model['satelliteAnomalyType'];
    }

    const filters = buildFilterQuery(model as Record<string, Object>) as FieldFilter[];

    this.appendCreateAtRangeFilters(filters, range);

    await this.regionManager.includeChildInRequest(this.filterModel as {regionId?: string}, filters as any);
    this.filtersParams = filters;
    this.toggleFilter = !this.toggleFilter;
  }

  // ─── Row helpers ───

  isSatelliteRow(row: unknown): boolean {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    return rec['rowKind'] === ROW_KIND_SATELLITE;
  }

  anomalyTypeLine(row: unknown): string | null {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const val = rec['anomalyTypeLine'];
    if (typeof val !== 'string') return null;
    const normalized = val.trim();
    return normalized && normalized !== '—' ? normalized : null;
  }

  anomalyDescriptionLine(row: unknown): string | null {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const val = rec['anomalyDescriptionLine'];
    return typeof val === 'string' && val.trim() ? val.trim() : null;
  }

  satelliteAnomalyType(row: unknown): number | null {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const val = rec['satelliteAnomalyType'];
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val.trim()) {
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  satelliteTypeLabel(typeVal: number | null): string | null {
    if (!typeVal) return null;
    const item = ANOMALY_LEGEND_ITEMS.find(i => i.anomalyType === typeVal);
    return item?.labelKey ?? null;
  }

  hasRecommendationFile(row: unknown): boolean {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    return rec['hasRecommendationFile'] === true;
  }

  reportIdForDownload(row: unknown): string | null {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const id = rec['reportId'] ?? rec['id'];
    return id != null && String(id).trim() ? String(id) : null;
  }

  /** Report id for download icon — only when backend says a file exists. */
  reportIdIfDownloadable(row: unknown): string | null {
    return this.hasRecommendationFile(row) ? this.reportIdForDownload(row) : null;
  }

  // ─── Legacy region/field/crop helpers (still valid because DTO has same shape) ───

  regionId(row: unknown): string | null {
    return reportRegionId(row);
  }

  fieldId(row: unknown): string | null {
    return reportFieldId(row);
  }

  fieldName(row: unknown): string | null {
    const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const name = typeof rec['fieldName'] === 'string' ? rec['fieldName'] : reportFieldName(row);
    return !name || name === '—' ? null : name;
  }

  cropLabel(cropId: string | null): string {
    if (!cropId) {
      return '—';
    }
    const name = this.cropCache.getCropNameById(cropId, true);
    return name && String(name).trim() ? String(name) : '—';
  }

  cropId(row: unknown): string | null {
    return reportSeasonCropId(row);
  }

}
